import { lazy, Suspense } from "react";
import { LoadState } from "@/components/LoadState";
import { routeAllowed, validUser } from "@/lib/authSession";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
const SupplierUpload = lazy(() => import("@/pages/SupplierUpload"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Supporto = lazy(() => import("@/pages/Supporto"));
const Aiuto = lazy(() => import("@/pages/Aiuto"));
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Imprese = lazy(() => import("@/pages/Imprese"));
const ImpresaDetail = lazy(() => import("@/pages/ImpresaDetail"));
const Lavoratori = lazy(() => import("@/pages/Lavoratori"));
const LavoratoreDetail = lazy(() => import("@/pages/LavoratoreDetail"));
const Cantieri = lazy(() => import("@/pages/Cantieri"));
const CantiereDetail = lazy(() => import("@/pages/CantiereDetail"));
const Presenze = lazy(() => import("@/pages/Presenze"));
const Documenti = lazy(() => import("@/pages/Documenti"));
const Carica = lazy(() => import("@/pages/Carica"));
const Scadenziario = lazy(() => import("@/pages/Scadenziario"));
const Mancanti = lazy(() => import("@/pages/Mancanti"));
const DaVerificare = lazy(() => import("@/pages/DaVerificare"));
const Report = lazy(() => import("@/pages/Report"));
const Impostazioni = lazy(() => import("@/pages/Impostazioni"));
const Audit = lazy(() => import("@/pages/Audit"));
const Tenants = lazy(() => import("@/pages/Tenants"));
const ConsumiAI = lazy(() => import("@/pages/ConsumiAI"));
import { Loader2 } from "lucide-react";

function Protected({ children, roles, permission }) {
  const { user, authError, retryAuth } = useAuth();
  if (authError) return <div role="alert" className="min-h-screen flex flex-col items-center justify-center gap-4">
    <p>{authError}</p><button className="border rounded px-4 py-2" onClick={retryAuth}>Riprova accesso</button>
  </div>;
  if (user === null) return <div role="status" aria-label="Verifica accesso" className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!validUser(user)) return <Navigate to="/login" replace />;
  const module = window.location.pathname.startsWith("/cantieri") ? "cantieri" : window.location.pathname.startsWith("/lavoratori") ? "lavoratori" : window.location.pathname.startsWith("/presenze") ? "presenze" : null;
  if (module && user.modules?.[module] === false) return <Layout><p role="alert">Modulo non attivo per questo cliente.</p></Layout>;
  if (!routeAllowed(user, roles, permission)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function RoleRoute({ roles, children }) {
  return <Protected roles={roles}>{children}</Protected>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<LoadState loading />}><Routes>
          <Route path="/consegna-documenti" element={<SupplierUpload />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/supporto" element={<Protected><Supporto /></Protected>} />
            <Route path="/aiuto" element={<Protected><Aiuto /></Protected>} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/imprese" element={<Protected><Imprese /></Protected>} />
            <Route path="/imprese/:id" element={<Protected><ImpresaDetail /></Protected>} />
            <Route path="/lavoratori" element={<Protected><Lavoratori /></Protected>} />
            <Route path="/lavoratori/:id" element={<Protected><LavoratoreDetail /></Protected>} />
            <Route path="/cantieri" element={<Protected><Cantieri /></Protected>} />
            <Route path="/cantieri/:id" element={<Protected><CantiereDetail /></Protected>} />
            <Route path="/presenze" element={<Protected permission="presenze"><Presenze /></Protected>} />
            <Route path="/documenti" element={<Protected><Documenti /></Protected>} />
            <Route path="/documenti/:id" element={<Protected><Documenti /></Protected>} />
            <Route path="/carica" element={<Protected><Carica /></Protected>} />
            <Route path="/scadenziario" element={<Protected><Scadenziario /></Protected>} />
            <Route path="/mancanti" element={<Protected><Mancanti /></Protected>} />
            <Route path="/da-verificare" element={<Protected><DaVerificare /></Protected>} />
            <Route path="/report" element={<Protected><Report /></Protected>} />
            <Route path="/impostazioni" element={<RoleRoute roles={["SUPER_ADMIN", "TENANT_ADMIN"]}><Impostazioni /></RoleRoute>} />
            <Route path="/audit" element={<RoleRoute roles={["SUPER_ADMIN", "TENANT_ADMIN"]}><Audit /></RoleRoute>} />
            <Route path="/tenant" element={<RoleRoute roles={["SUPER_ADMIN"]}><Tenants /></RoleRoute>} />
            <Route path="/consumi-ai" element={<RoleRoute roles={["SUPER_ADMIN"]}><ConsumiAI /></RoleRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes></Suspense>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
